import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

/**
 * Category breaks for grouped collection pages (Alle Produkte, Core, Top-Tier).
 *
 * Product cards carry data-category / data-category-url (see main-collection.liquid).
 * The collection is manually sorted so categories form contiguous segments;
 * this element injects a full-width break tile before each segment and keeps
 * the tiles correct while paginated-list.js appends/prepends further pages.
 *
 * Breaks are injected client-side only and deliberately do NOT use
 * ref="cards[]": paginated-list.js selects [ref="cards[]"] from fetched page
 * HTML, while already-injected DOM nodes survive append/prepend untouched.
 * Staying out of refs.cards also keeps its page math (data-page on first/last
 * card) intact.
 *
 * Breaks are suppressed while a filter is active or a non-default sort is
 * selected, because both destroy the contiguous category order.
 */
class CategoryBreaks extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #observer;

  /** @type {number | undefined} */
  #scheduled;

  connectedCallback() {
    this.#apply();

    const grid = this.#grid;
    if (grid) {
      this.#observer = new MutationObserver(() => this.#schedule());
      this.#observer.observe(grid, { childList: true });
    }

    document.addEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
  }

  #onFilterUpdate = () => this.#schedule();

  #schedule() {
    if (this.#scheduled) return;
    this.#scheduled = requestAnimationFrame(() => {
      this.#scheduled = undefined;
      this.#apply();
    });
  }

  get #grid() {
    return this.closest('results-list')?.querySelector('[ref="grid"]') ?? null;
  }

  get #enabled() {
    const params = new URLSearchParams(window.location.search);
    if (params.has('sort_by') && params.get('sort_by') !== 'manual') return false;
    for (const key of params.keys()) {
      if (key.startsWith('filter.')) return false;
    }
    return true;
  }

  #apply() {
    const grid = this.#grid;
    if (!grid) return;

    if (!this.#enabled) {
      for (const el of grid.querySelectorAll(':scope > .product-grid__break')) el.remove();
      return;
    }

    let lastCategory = null;
    for (const child of Array.from(grid.children)) {
      if (child.classList.contains('product-grid__break')) {
        // Obsolete when it no longer sits on a boundary; #ensureBreak re-adds
        // the correct one right after, so removal here is safe.
        const next = child.nextElementSibling;
        const isBoundary =
          next instanceof HTMLElement && next.dataset.category && next.dataset.category !== lastCategory;
        if (!isBoundary) child.remove();
        continue;
      }

      if (!(child instanceof HTMLElement) || !child.dataset.category) continue;

      const category = child.dataset.category;
      if (category !== lastCategory) {
        this.#ensureBreak(child, category);
        lastCategory = category;
      }
    }
  }

  /**
   * @param {HTMLElement} card - first card of a segment
   * @param {string} category
   */
  #ensureBreak(card, category) {
    const prev = card.previousElementSibling;
    if (prev?.classList.contains('product-grid__break') && prev.getAttribute('data-category') === category) {
      return;
    }

    const li = document.createElement('li');
    li.className = 'product-grid__break';
    li.id = `cat-${this.#handleize(category)}`;
    li.setAttribute('data-category', category);

    const title = document.createElement('h2');
    title.className = 'product-grid__break-title h4';
    title.textContent = category;
    li.append(title);

    const url = card.dataset.categoryUrl;
    if (url) {
      const link = document.createElement('a');
      link.className = 'product-grid__break-link';
      link.href = url;
      link.textContent = 'Alle ansehen';
      li.append(link);
    }

    card.before(li);
  }

  /**
   * Mirrors Shopify's handleize filter (chips build their anchor ids with it
   * in Liquid), including diacritic stripping: ö → o, ä → a.
   * @param {string} value
   */
  #handleize(value) {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
}

/**
 * Anchor chips ("Springe zu") for grouped collection pages.
 *
 * A chip scrolls to its category break. When the segment isn't rendered yet,
 * remaining pages are loaded through the section renderer first (the catalog
 * is small, at most a handful of pages). When the category doesn't exist in
 * this collection at all, the chip falls back to its href (the category
 * collection page).
 */
class CategoryChips extends HTMLElement {
  #loading = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  get #resultsList() {
    return this.closest('results-list');
  }

  get #grid() {
    return this.#resultsList?.querySelector('[ref="grid"]') ?? null;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const chip = event.target instanceof Element ? event.target.closest('.category-chips__chip') : null;
    if (!chip || !(chip instanceof HTMLAnchorElement)) return;

    const anchor = chip.dataset.anchor;
    if (!anchor || this.#loading) return;

    const existing = document.getElementById(anchor);
    if (existing) {
      event.preventDefault();
      this.#scrollTo(existing);
      return;
    }

    event.preventDefault();
    this.#loadUntilFound(chip, anchor);
  };

  /**
   * @param {HTMLAnchorElement} chip
   * @param {string} anchor
   */
  async #loadUntilFound(chip, anchor) {
    const grid = this.#grid;
    const sectionId = this.#resultsList?.getAttribute('section-id');
    if (!grid || !sectionId) {
      window.location.href = chip.href;
      return;
    }

    this.#loading = true;
    chip.setAttribute('aria-busy', 'true');

    try {
      const lastPage = Number(grid.dataset.lastPage || '1');
      for (let guard = 0; guard < lastPage; guard += 1) {
        const target = document.getElementById(anchor);
        if (target) {
          this.#scrollTo(target);
          return;
        }

        const cards = grid.querySelectorAll(':scope > [ref="cards[]"]');
        const lastCard = cards[cards.length - 1];
        if (!(lastCard instanceof HTMLElement)) break;

        const nextPage = Number(lastCard.dataset.page || '1') + 1;
        if (nextPage > lastPage) break;
        if (grid.querySelector(`:scope > [ref="cards[]"][data-page="${nextPage}"]`)) continue;

        const url = new URL(window.location.href);
        url.searchParams.set('page', String(nextPage));
        url.hash = '';

        const html = await sectionRenderer.getSectionHTML(sectionId, true, url);
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fetchedGrid = parsed.querySelector('[ref="grid"]');
        const items = fetchedGrid?.querySelectorAll(':scope > [ref="cards[]"]');
        if (!items || items.length === 0) break;
        grid.append(...items);

        // Give the break injector a frame to process the new cards.
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      }

      const target = document.getElementById(anchor);
      if (target) {
        this.#scrollTo(target);
      } else {
        window.location.href = chip.href;
      }
    } finally {
      this.#loading = false;
      chip.removeAttribute('aria-busy');
    }
  }

  /** @param {HTMLElement} target */
  #scrollTo(target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

if (!customElements.get('category-breaks')) customElements.define('category-breaks', CategoryBreaks);
if (!customElements.get('category-chips')) customElements.define('category-chips', CategoryChips);
